import * as Sentry from "@sentry/react";
import { useState, useEffect, useCallback, useRef } from "react";
import type { jscanify } from "../types/jscanify";

export type ScannerErrorKind =
  | "unsupported-webassembly"
  | "opencv-load-failed"
  | "opencv-timeout"
  | "jscanify-load-failed"
  | "jscanify-unavailable"
  | "unknown";

export interface ScannerDiagnostics {
  webAssembly: boolean;
  createImageBitmap: boolean;
  worker: boolean;
  secureContext: boolean;
  crossOriginIsolated: boolean;
  hardwareConcurrency?: number;
  deviceMemory?: number;
}

interface UseJscanifyReturn {
  scanner: jscanify | null;
  isLoading: boolean;
  error: string | null;
  errorKind: ScannerErrorKind | null;
  diagnostics: ScannerDiagnostics;
  retry: () => void;
}

// Track loading state globally to avoid multiple script loads
let opencvLoadPromise: Promise<void> | null = null;
let jscanifyLoadPromise: Promise<void> | null = null;
let scannerInitializationAttemptId = 0;
let capturedScannerInitializationFailureAttemptId: number | null = null;

class ScannerInitializationError extends Error {
  constructor(
    readonly kind: ScannerErrorKind,
    message: string,
  ) {
    super(message);
    this.name = "ScannerInitializationError";
  }
}

const getNavigatorNumber = (key: string): number | undefined => {
  if (typeof navigator === "undefined") {
    return undefined;
  }

  const value = Reflect.get(navigator, key);
  return typeof value === "number" ? value : undefined;
};

const getScannerDiagnostics = (): ScannerDiagnostics => ({
  webAssembly:
    typeof WebAssembly === "object" &&
    typeof WebAssembly.instantiate === "function",
  createImageBitmap: typeof createImageBitmap === "function",
  worker: typeof Worker === "function",
  secureContext: typeof window !== "undefined" && window.isSecureContext,
  crossOriginIsolated:
    typeof window !== "undefined" && window.crossOriginIsolated,
  hardwareConcurrency: getNavigatorNumber("hardwareConcurrency"),
  deviceMemory: getNavigatorNumber("deviceMemory"),
});

const getScannerErrorMessage = (kind: ScannerErrorKind): string => {
  switch (kind) {
    case "unsupported-webassembly":
      return "Tu navegador no permite preparar el recorte automático. Puedes ajustar las esquinas y continuar con un recorte básico o usar el original.";
    case "opencv-timeout":
      return "El recorte automático tardó demasiado en prepararse. Puedes ajustar las esquinas y continuar con un recorte básico, reintentar o usar el original.";
    case "opencv-load-failed":
    case "jscanify-load-failed":
    case "jscanify-unavailable":
    case "unknown":
      return "El recorte automático no se pudo preparar. Puedes ajustar las esquinas y continuar con un recorte básico, reintentar o usar el original.";
  }
};

const classifyScannerError = (err: unknown): ScannerErrorKind => {
  if (err instanceof ScannerInitializationError) {
    return err.kind;
  }

  const message = err instanceof Error ? err.message.toLowerCase() : "";

  if (message.includes("webassembly")) {
    return "unsupported-webassembly";
  }
  if (message.includes("timeout")) {
    return "opencv-timeout";
  }
  if (message.includes("opencv")) {
    return "opencv-load-failed";
  }
  if (message.includes("jscanify")) {
    return "jscanify-load-failed";
  }

  return "unknown";
};

const startScannerInitializationAttempt = (): void => {
  scannerInitializationAttemptId += 1;
  capturedScannerInitializationFailureAttemptId = null;
};

const captureScannerInitializationFailure = (
  err: unknown,
  kind: ScannerErrorKind,
  diagnostics: ScannerDiagnostics,
): void => {
  if (
    capturedScannerInitializationFailureAttemptId ===
    scannerInitializationAttemptId
  ) {
    return;
  }

  capturedScannerInitializationFailureAttemptId =
    scannerInitializationAttemptId;

  const diagnosticsContext: Record<string, unknown> = { ...diagnostics };

  Sentry.withScope((scope) => {
    scope.setTag("feature", "document_scanner");
    scope.setTag("scanner_error_kind", kind);
    scope.setContext("scanner_diagnostics", diagnosticsContext);
    Sentry.captureException(
      err instanceof Error
        ? err
        : new Error("Document scanner initialization failed"),
    );
  });
};

/**
 * Dynamically load a script and return a promise that resolves when loaded
 */
const loadScript = (src: string, id: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Check if script already exists
    const existingScript = document.getElementById(id);
    if (existingScript) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.id = id;
    script.src = src;
    script.async = true;

    script.onload = () => resolve();
    script.onerror = () => {
      script.remove();
      reject(new Error(`Failed to load script: ${src}`));
    };

    document.head.appendChild(script);
  });
};

/**
 * Wait for OpenCV runtime to be fully initialized
 */
const waitForOpenCVRuntime = (timeoutMs: number = 30000): Promise<void> => {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();

    const checkReady = () => {
      // Check if OpenCV is fully ready
      if (
        window.cv &&
        typeof window.cv.imread === "function" &&
        window.cv.Mat
      ) {
        resolve();
        return;
      }

      // Set up runtime callback if cv exists but isn't ready
      if (window.cv && !window.cv.Mat) {
        const existingCallback = window.cv.onRuntimeInitialized;
        window.cv.onRuntimeInitialized = () => {
          existingCallback?.();
          resolve();
        };
        return;
      }

      // Timeout check
      if (Date.now() - startTime > timeoutMs) {
        reject(
          new ScannerInitializationError(
            "opencv-timeout",
            "OpenCV runtime initialization timeout",
          ),
        );
        return;
      }

      // Keep polling
      setTimeout(checkReady, 100);
    };

    checkReady();
  });
};

/**
 * Load OpenCV.js with lazy loading (only loads once globally)
 */
const loadOpenCV = (): Promise<void> => {
  if (window.cv && typeof window.cv.imread === "function") {
    return Promise.resolve();
  }

  const diagnostics = getScannerDiagnostics();
  if (!diagnostics.webAssembly) {
    startScannerInitializationAttempt();
    return Promise.reject(
      new ScannerInitializationError(
        "unsupported-webassembly",
        "WebAssembly is not available",
      ),
    );
  }

  if (!opencvLoadPromise) {
    startScannerInitializationAttempt();
    opencvLoadPromise = loadScript("/libs/opencv.js", "opencv-js")
      .then(() => waitForOpenCVRuntime())
      .catch((error) => {
        opencvLoadPromise = null; // Allow retry on failure
        if (error instanceof ScannerInitializationError) {
          throw error;
        }
        throw new ScannerInitializationError(
          "opencv-load-failed",
          error instanceof Error ? error.message : "Failed to load OpenCV",
        );
      });
  }

  return opencvLoadPromise;
};

/**
 * Load jscanify.js with lazy loading (only loads once globally)
 */
const loadJscanify = (): Promise<void> => {
  if (window.jscanify) {
    return Promise.resolve();
  }

  if (!jscanifyLoadPromise) {
    startScannerInitializationAttempt();
    jscanifyLoadPromise = loadScript("/libs/jscanify.js", "jscanify-js").catch(
      (error) => {
        jscanifyLoadPromise = null; // Allow retry on failure
        throw new ScannerInitializationError(
          "jscanify-load-failed",
          error instanceof Error ? error.message : "Failed to load jscanify",
        );
      },
    );
  }

  return jscanifyLoadPromise;
};

/**
 * Hook to load and initialize jscanify document scanner
 * Lazily loads OpenCV.js and jscanify.js only when needed
 * Includes proper cleanup to prevent memory leaks and race conditions
 * @returns {UseJscanifyReturn} Scanner instance, loading state, error, and retry function
 */
export const useJscanify = (): UseJscanifyReturn => {
  const [scanner, setScanner] = useState<jscanify | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<ScannerErrorKind | null>(null);
  const [diagnostics, setDiagnostics] = useState<ScannerDiagnostics>(() =>
    getScannerDiagnostics(),
  );
  const [retryCount, setRetryCount] = useState(0);
  const isMountedRef = useRef(true);

  const retry = useCallback(() => {
    // Reset global promises to allow fresh load attempt
    opencvLoadPromise = null;
    jscanifyLoadPromise = null;
    setError(null);
    setErrorKind(null);
    setDiagnostics(getScannerDiagnostics());
    setIsLoading(true);
    setScanner(null);
    setRetryCount((c) => c + 1);
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    const initializeScanner = async () => {
      try {
        // Load both libraries (OpenCV must load first as jscanify depends on it)
        await loadOpenCV();

        if (!isMountedRef.current) return;

        await loadJscanify();

        if (!isMountedRef.current) return;

        // Verify jscanify is available
        if (!window.jscanify) {
          throw new ScannerInitializationError(
            "jscanify-unavailable",
            "jscanify not available after loading",
          );
        }

        const scannerInstance = new window.jscanify();

        if (isMountedRef.current) {
          setScanner(scannerInstance);
          setIsLoading(false);
          setError(null);
          setErrorKind(null);
          setDiagnostics(getScannerDiagnostics());
        }
      } catch (err) {
        if (!isMountedRef.current) return;

        const initializationError =
          err instanceof Error
            ? err
            : new ScannerInitializationError(
                "unknown",
                "Document scanner initialization failed",
              );
        const currentDiagnostics = getScannerDiagnostics();
        const kind = classifyScannerError(initializationError);
        captureScannerInitializationFailure(
          initializationError,
          kind,
          currentDiagnostics,
        );

        setError(getScannerErrorMessage(kind));
        setErrorKind(kind);
        setDiagnostics(currentDiagnostics);
        setScanner(null);
        setIsLoading(false);
      }
    };

    initializeScanner();

    return () => {
      isMountedRef.current = false;
    };
  }, [retryCount]);

  return { scanner, isLoading, error, errorKind, diagnostics, retry };
};
